import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Checkbox from 'components/Form/Checkbox';
import FormActions from 'components/Form/FormActions';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import InputHidden from 'components/Form/InputHidden';
import SubmitButton from 'components/Form/SubmitButton';
import Stacked from 'components/Layout/Stacked';
import {useBackend} from 'state/backend';
import {FormFields} from 'types/utils';
import {onApiFormError, onApiKeySuccess} from 'utils/request/result';
import {
    ProfileNotificationEditFormFields,
    usePostProfileNotificationEdit,
} from './services';
import {ProfileNotificationEditBackend} from './types';

type ProfileNotificationEditFormData =
    FormFields<ProfileNotificationEditFormFields>;

const ProfileNotificationEdit: FC = () => {
    const methods = useForm<ProfileNotificationEditFormData>();
    const {clearErrors, handleSubmit} = methods;

    const {mutateAsync} = usePostProfileNotificationEdit();

    const {
        data: {email, isNewsletterEnabled, isOnboardingEnabled, teamContext},
    } = useBackend<ProfileNotificationEditBackend>();

    const {t} = useTranslation('Profile.Notification.Edit');

    const onSubmit = async (formData: ProfileNotificationEditFormData) => {
        clearErrors();
        await mutateAsync(formData)
            .then((response) => onApiKeySuccess(methods)(response))
            .catch(onApiFormError(methods));
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stacked className="max-w-xl">
                    <FormHeader
                        subtitle={t('description', {userEmail: email})}
                        title={t('title')}
                    />
                    {teamContext && (
                        <InputHidden name="teamContext" value="True" />
                    )}
                    <Checkbox
                        defaultChecked={true}
                        disabled={true}
                        helpText={t('systemNotificationsHelper')}
                        label={t('systemNotifications')}
                        name="disabledDefaultSystemNotifications"
                    />
                    <Checkbox
                        defaultChecked={isNewsletterEnabled}
                        helpText={t('announcementsHelper')}
                        label={t('announcements')}
                        name="isNewsletterEnabled"
                    />
                    <Checkbox
                        defaultChecked={isOnboardingEnabled}
                        helpText={t('onboardingHelper')}
                        label={t('onboarding')}
                        name="isOnboardingEnabled"
                    />
                    <FormResult />
                    <FormActions align="left">
                        <SubmitButton>{t('save')}</SubmitButton>
                    </FormActions>
                </Stacked>
            </form>
        </FormProvider>
    );
};

export default ProfileNotificationEdit;
